import {
  GuidEmpty,
  IntMaxValue,
  MaxFileSizeForBackend,
  MaxFileSizeStrFormat,
  MaxTicks,
  TimeSpanMaxValue,
  TimeSpanZeroValue
} from 'mbs-ui-kit/utils/constants';
import { PasswordFromPlan } from 'mbs-ui-shared@constants/misc-constants';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { RecurringSchedule } from 'mbs-ui-shared@models/ScheduleModel';
import { ScheduleStepValue, ScheduleType } from 'mbs-ui-shared@models/backup/schedule-models';
import { RemoteDeployPlanModel } from 'mbs-ui-shared@models/remote-deploy/remote-deploy-plan-model';
import { multiplyNumbersInStringFormat } from 'mbs-ui-shared@utils/bigMath';
import {
  GetDateFromTimeString,
  GetTimeSpanString,
  TimeGroupToTimeSpanString,
  getFullDateFromDateTimeObject,
  getISODateFromTimeAndDate
} from 'mbs-ui-shared@utils/date';
import { ScheduleDataHelper } from 'mbs-ui-shared@utils/schedule-data-helper';
import moment from 'moment';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { AdvancedFilterStepValue, FilterTypes } from 'wizards/models/advanced-filters-models';
import { BaseRetentionPolicy, FromTime, RetentionPolicyStepValue } from 'wizards/models/retention-policy-models';
import { ScheduleAdvancedStepValue, ScheduleSettings } from 'wizards/models/schedule-advanced-models';
import { SimpleScheduleStepValue } from 'wizards/models/simple-schedule-models';
import { VirtualDiskFileFormat } from 'wizards/models/type-data-models';
import { BackupTargetVolumesEnum } from 'wizards/models/what-backup-models';

export type EncodeMode = 'hex' | 'int32' | 'int64';

export class SupportMethodsForPlanFromSteps {
  public static getExtentBasedId(extents: any[]): string {
    let extentBasedId = '';
    extentBasedId += extents.length.toString();
    extents.forEach(function (extent) {
      extentBasedId += extent.Length.toString();
      extentBasedId += extent.StartOffset.toString();
    });
    return extentBasedId;
  }

  public static updateNewPlanDiskInfo(newPlan): void {
    if (newPlan.DiskInfo && newPlan.DiskInfo.length > 0) {
      newPlan.DiskInfo.forEach((diskInfo) => {
        if (diskInfo && diskInfo.Volumes) {
          diskInfo.Volumes.forEach((vol) => {
            if (vol && vol.BackupOptions) vol.BackupOptions.ExcludeRules = [];
          });
        }
      });
    }
  }

  public static getDiskImageFileFormat(fileFormat): VirtualDiskFileFormat {
    const newElement = { Name: '', VariantName: '', DisplayName: fileFormat, UseBuilder: false };
    switch (fileFormat) {
      case 'Hyper-V Virtual Disk (VHD-format) fixed':
        newElement.Name = 'VHD';
        newElement.VariantName = 'fixed';
        newElement.UseBuilder = true;
        break;
      case 'Hyper-V Virtual Disk (VHD-format) dynamic':
        newElement.Name = 'VHD';
        newElement.VariantName = 'dynamic';
        newElement.UseBuilder = true;
        break;
      case 'RAW disk image raw':
        newElement.Name = 'RAW';
        newElement.VariantName = 'raw';
        newElement.UseBuilder = true;
        break;
      case 'RAW disk image rawsparse':
        newElement.Name = 'RAW';
        newElement.VariantName = 'rawsparse';
        newElement.UseBuilder = true;
        break;
      case 'RAW disk image tar':
        newElement.Name = 'RAW';
        newElement.VariantName = 'tar';
        newElement.UseBuilder = true;
        break;
      case 'RAW disk image tgz':
        newElement.Name = 'RAW';
        newElement.VariantName = 'tgz';
        newElement.UseBuilder = true;
        break;
      case 'VMware Virtual Disk vmfsfixed':
        newElement.Name = 'VMDK';
        newElement.VariantName = 'vmfsfixed';
        break;
      case 'VMware Virtual Disk vmfsdynamic':
        newElement.Name = 'VMDK';
        newElement.VariantName = 'vmfsdynamic';
        break;
      case 'Hyper-V Virtual Disk (VHDX-format) dynamic':
        newElement.Name = 'VHDX';
        newElement.VariantName = 'dynamic';
        newElement.UseBuilder = true;
        break;
      case 'VirtualBox Virtual Disk fixed':
        newElement.Name = 'VDI';
        newElement.VariantName = 'fixed';
        break;
      case 'VirtualBox Virtual Disk dynamic':
        newElement.Name = 'VDI';
        newElement.VariantName = 'dynamic';
        break;
    }
    return newElement;
  }

  public static getFiltersArray(step: AdvancedFilterStepValue): string[] {
    if (step.backupAllFiles === FilterTypes.UseExcludeMask) {
      const separatorExc = ~step.excludeMask.indexOf(',') ? ',' : ';';
      return step.excludeMask.split(separatorExc).map((s) => s.trim());
    } else if (step.backupAllFiles === FilterTypes.UseIncludeMask) {
      const separatorInc = ~step.includeMask.indexOf(',') ? ',' : ';';
      return step.includeMask.split(separatorInc).map((s) => s.trim());
    } else return [];
  }

  public static fromMBToBytes(mbs: string): string {
    const first = multiplyNumbersInStringFormat(mbs, '1024');
    return multiplyNumbersInStringFormat(first, '1024');
  }

  public static planDataFromRetentionPolicy(
    newPlan,
    step: RetentionPolicyStepValue | BaseRetentionPolicy,
    isHybrid = false,
    isUnix = false,
    isIbb = false
  ): void {
    newPlan.RetentionUseDefaultSettings = !isHybrid ? step.RetentionUseDefaultSettings : !step.RetentionUseDefaultSettings;
    if (!newPlan.RetentionUseDefaultSettings) {
      newPlan.RetentionNumberOfVersions = !step.keepNumberOfVersions ? IntMaxValue : step.RetentionNumberOfVersions;
      newPlan.SerializationSupportRetentionTime = step.deleteVersionsOlderThan
        ? TimeGroupToTimeSpanString({ value: step.deleteVersionsOlderThanCount, period: step.deleteVersionsOlderThanPeriod })
        : TimeSpanMaxValue;

      if (!isIbb) {
        if (!(step.alwaysKeepLastVersion === null || step.alwaysKeepLastVersion === undefined))
          newPlan.RetentionDeleteLastVersion = !step.alwaysKeepLastVersion;
        if (!isUnix) {
          newPlan.SerializationSupportRetentionDelay = step.delayPurgeForEnabled
            ? TimeGroupToTimeSpanString({ value: step.delayPurgeFor, period: step.delayPurgeForSelect })
            : TimeSpanZeroValue;
        }
        if (!(step.fromTimeSelected === null || step.fromTimeSelected === undefined)) {
          const isBackupDate = step.fromTimeSelected === FromTime['backup date'];
          newPlan.UseBackupDate = isBackupDate;
          if (isBackupDate) newPlan.RetentionDeleteLastVersion = false;
        }
      }
    }
  }

  public static delStopAfterTickFromPlanForRD(newPlan): void {
    if (!newPlan.isArchive) {
      if (newPlan.Schedule && newPlan.Schedule.StopAfterTicks) {
        if (!newPlan.Schedule.StopAfter && newPlan.Schedule.StopAfterTicks !== +MaxTicks) {
          newPlan.Schedule.StopAfter = GetTimeSpanString(moment.duration(newPlan.Schedule.StopAfterTicks / 10000, 'milliseconds'));
        }
        delete newPlan.Schedule.StopAfterTicks;
      }
      if (newPlan.ForceFullSchedule && newPlan.ForceFullSchedule.StopAfterTicks && !newPlan.ForceFullSchedule.StopAfter) {
        if (!newPlan.ForceFullSchedule.StopAfter && newPlan.ForceFullSchedule.StopAfterTicks !== +MaxTicks) {
          newPlan.ForceFullSchedule.StopAfter = GetTimeSpanString(
            moment.duration(newPlan.ForceFullSchedule.StopAfterTicks / 10000, 'milliseconds')
          );
        }
        delete newPlan.ForceFullSchedule.StopAfterTicks;
      }
      if (newPlan.ScheduleDiff && newPlan.ScheduleDiff.StopAfterTicks) delete newPlan.ScheduleDiff.StopAfterTicks;
      if (newPlan.ScheduleTLog && newPlan.ScheduleTLog.StopAfterTicks) delete newPlan.ScheduleTLog.StopAfterTicks;
      if (newPlan.FfiSyntheticSchedule && newPlan.FfiSyntheticSchedule.StopAfterTicks) {
        if (!newPlan.FfiSyntheticSchedule.StopAfter && newPlan.FfiSyntheticSchedule.StopAfterTicks !== +MaxTicks) {
          newPlan.FfiSyntheticSchedule.StopAfter = GetTimeSpanString(
            moment.duration(newPlan.FfiSyntheticSchedule.StopAfterTicks / 10000, 'milliseconds')
          );
        }
        delete newPlan.FfiSyntheticSchedule.StopAfterTicks;
      }
    } else {
      if (newPlan.Schedule && newPlan.Schedule.StopAfterTicks) delete newPlan.Schedule.StopAfterTicks;
      if (newPlan.ForceFullSchedule && newPlan.ForceFullSchedule.StopAfterTicks) delete newPlan.ForceFullSchedule.StopAfterTicks;
      if (newPlan.FfiSyntheticSchedule && newPlan.FfiSyntheticSchedule.StopAfterTicks) delete newPlan.FfiSyntheticSchedule.StopAfterTicks;
    }
  }

  public static setStopAfterFromTick(newPlan): void {
    if (newPlan.Schedule && newPlan.Schedule.StopAfterTicks && !newPlan.Schedule.StopAfter) {
      const isStopAfter =
        (newPlan.Schedule.StopAfter && newPlan.Schedule.StopAfter !== TimeSpanMaxValue) ||
        (newPlan.Schedule.StopAfterTicks && newPlan.Schedule.StopAfterTicks.toString() !== MaxTicks);

      newPlan.Schedule.StopAfter = isStopAfter
        ? GetTimeSpanString(moment.duration(newPlan.Schedule.StopAfterTicks / 10000, 'milliseconds'))
        : TimeSpanMaxValue;
    }
    if (newPlan.ForceFullSchedule && newPlan.ForceFullSchedule.StopAfterTicks && !newPlan.ForceFullSchedule.StopAfter) {
      newPlan.ForceFullSchedule.StopAfter = GetTimeSpanString(
        moment.duration(newPlan.ForceFullSchedule.StopAfterTicks / 10000, 'milliseconds')
      );
    }
    if (newPlan.ScheduleDiff && newPlan.ScheduleDiff.StopAfterTicks && !newPlan.ScheduleDiff.StopAfter) {
      newPlan.ScheduleDiff.StopAfter = GetTimeSpanString(moment.duration(newPlan.ScheduleDiff.StopAfterTicks / 10000, 'milliseconds'));
    }
    if (newPlan.ScheduleTLog && newPlan.ScheduleTLog.StopAfterTicks && !newPlan.ScheduleTLog.StopAfter) {
      newPlan.ScheduleTLog.StopAfter = GetTimeSpanString(moment.duration(newPlan.ScheduleTLog.StopAfterTicks / 10000, 'milliseconds'));
    }
    if (newPlan.FfiSyntheticSchedule?.StopAfterTicks && !newPlan.FfiSyntheticSchedule.StopAfter) {
      newPlan.FfiSyntheticSchedule.StopAfter =
        newPlan.FfiSyntheticSchedule.StopAfterTicks.toString() !== MaxTicks
          ? GetTimeSpanString(moment.duration(newPlan.FfiSyntheticSchedule.StopAfterTicks / 10000, 'milliseconds'))
          : TimeSpanMaxValue;
    }
  }

  public static normalizePlanForSaveConfig(plan: any, isDisabledPlansTab: boolean): void {
    this.setStopAfterFromTick(plan.plan);
    this.delStopAfterTickFromPlan(plan.plan);

    if (plan.plan.MaxFileSize && plan.plan.MaxFileSize.toString() === MaxFileSizeStrFormat) plan.plan.MaxFileSize = MaxFileSizeForBackend;
    if (!isDisabledPlansTab && plan.invalidForRd) SupportMethodsForPlanFromSteps.updateInvalidPlanForRd(plan);
    if (plan.plan.ForceFullSchedule && !plan.plan.ForceFullSchedule.Enabled) plan.plan.ForceFullSchedule = { Enabled: false };
    if (plan.plan.ScheduleDiff && !plan.plan.ScheduleDiff.Enabled) plan.plan.ScheduleDiff = { Enabled: false };
    if (plan.plan.ScheduleTLog && !plan.plan.ScheduleTLog.Enabled) plan.plan.ScheduleTLog = { Enabled: false };
    if (plan.plan.Actions) {
      if (plan.plan.Actions.Pre && plan.plan.Actions.Pre.Arguments) {
        plan.plan.Actions.Pre.Arguments = plan.plan.Actions.Pre.Arguments.trim();
        plan.plan.Actions.Pre.CommandLine = plan.plan.Actions.Pre.CommandLine.trim();
        plan.plan.Actions.Pre.CommandLine = SupportMethodsForStepsFromPlan.getActionString(plan.plan.Actions.Pre);
        delete plan.plan.Actions.Pre.Arguments;
      }
      if (plan.plan.Actions.Post && plan.plan.Actions.Post.Arguments) {
        plan.plan.Actions.Post.Arguments = plan.plan.Actions.Post.Arguments.trim();
        plan.plan.Actions.Post.CommandLine = plan.plan.Actions.Post.CommandLine.trim();
        plan.plan.Actions.Post.CommandLine = SupportMethodsForStepsFromPlan.getActionString(plan.plan.Actions.Post);
        delete plan.plan.Actions.Post.Arguments;
      }
    }

    if (plan.plan?.Schedule?.StopAfter) {
      plan.plan.ForceFullSchedule.StopAfter = plan.plan.Schedule.StopAfter;
    }

    delete plan.invalidForRd;
  }

  public static delStopAfterTickFromPlan(newPlan): void {
    if (newPlan.Schedule?.StopAfterTicks) delete newPlan.Schedule.StopAfterTicks;
    if (newPlan.ForceFullSchedule?.StopAfterTicks) delete newPlan.ForceFullSchedule.StopAfterTicks;
    if (newPlan.ScheduleDiff?.StopAfterTicks) delete newPlan.ScheduleDiff.StopAfterTicks;
    if (newPlan.ScheduleTLog?.StopAfterTicks) delete newPlan.ScheduleTLog.StopAfterTicks;
    if (newPlan.FfiSyntheticSchedule?.StopAfterTicks) delete newPlan.FfiSyntheticSchedule.StopAfterTicks;
  }

  public static updateInvalidPlanForRd(wrapper: RemoteDeployPlanModel): void {
    if (wrapper.PlanType === PlanTypes.BackupDiskImagePlan && wrapper.plan.BackupVolumes === BackupTargetVolumesEnum.SelectedOnly) {
      wrapper.plan.BackupVolumes = BackupTargetVolumesEnum.SystemRequired;
    }
    if (wrapper.plan.IsHybridBackup) {
      wrapper.plan.IsHybridBackup = false;
      if (!(wrapper.plan.SyntheticFull === null || wrapper.plan.SyntheticFull === undefined)) wrapper.plan.SyntheticFull = true;
    }
    if (wrapper.plan.ExcludeEnabled) wrapper.plan.ExcludeEnabled = false;
    if (wrapper.plan.HybridID && wrapper.plan.HybridID !== GuidEmpty) wrapper.plan.HybridID = GuidEmpty;
    if (wrapper.plan.DiskInfo && wrapper.plan.DiskInfo.length) wrapper.plan.DiskInfo = [];
    if (wrapper.plan.HybridRetentionPolicy && !wrapper.plan.HybridRetentionPolicy.RetentionUseDefaultSettings)
      wrapper.plan.HybridRetentionPolicy.RetentionUseDefaultSettings = true;
  }

  public static updatePlanIfNoSchedule(plan, stopAfterString, isRestore = false): void {
    if (!isRestore) {
      plan.ScheduleDiff = { Enabled: false } as ScheduleSettings;
      plan.ScheduleTLog = { Enabled: false } as ScheduleSettings;
      plan.Schedule = { Enabled: false } as ScheduleSettings;
      plan.ForceFullSchedule = { Enabled: false } as ScheduleSettings;
      plan.Schedule.StopAfter = stopAfterString;
      plan.ForceFullSchedule.StopAfter = stopAfterString;
    } else {
      plan.Schedule = { Enabled: false } as ScheduleSettings;
      plan.Schedule.StopAfter = stopAfterString;
    }
  }

  public static updatePlanIfOnce(plan, schedule, OnceDate, stopAfterString, isRestore = false): void {
    schedule.OnceDate = getISODateFromTimeAndDate(OnceDate.time.toString(), OnceDate.date);
    schedule.Enabled = true;
    schedule.RecurType = RecurType.Once;
    schedule.StopAfter = stopAfterString;
    if (!isRestore) {
      plan.Schedule = { Enabled: false } as ScheduleSettings;
      plan.Schedule.StopAfter = stopAfterString;
      plan.ScheduleDiff = { Enabled: false } as ScheduleSettings;
      plan.ScheduleTLog = { Enabled: false } as ScheduleSettings;
    }
  }

  public static updatePlanIfHasAdvancedForUnix(ScheduleModelAdvanced, schedules, stopAfterString, isUnix = false): void {
    if (ScheduleModelAdvanced.blockLevel && ScheduleModelAdvanced.blockLevel.Enabled) {
      ScheduleDataHelper.SetRecurringScheduleSettings(schedules.schedule, ScheduleModelAdvanced.forceFull.FormSchedule, isUnix);
      ScheduleDataHelper.SetRecurringScheduleSettings(schedules.fullSchedule, ScheduleModelAdvanced.blockLevel.FormSchedule, isUnix);
    } else if (ScheduleModelAdvanced.forceFull.Enabled)
      ScheduleDataHelper.SetRecurringScheduleSettings(schedules.schedule, ScheduleModelAdvanced.forceFull.FormSchedule, isUnix);
    else ScheduleDataHelper.SetRecurringEmptySchedule(schedules.fullSchedule, isUnix);
    this.setStopAfterForSchedules(schedules, stopAfterString);
  }

  public static updatePlanIfHasAdvancedForRestores(ScheduleModelAdvanced, schedule, stopAfterString, isUnix = false): void {
    if (schedule) {
      if (ScheduleModelAdvanced.forceFull && ScheduleModelAdvanced.forceFull.Enabled)
        ScheduleDataHelper.SetRecurringScheduleSettings(schedule, ScheduleModelAdvanced.forceFull.FormSchedule, isUnix);
      else ScheduleDataHelper.SetRecurringEmptySchedule(schedule, isUnix);
      schedule.StopAfter = stopAfterString;
    }
  }

  public static updatePlanPassword(newPlan, password: string): void {
    newPlan.EncryptionPassword = password;

    if (newPlan.EncryptionPassword === PasswordFromPlan) {
      newPlan.EncryptionPassword = null;
    }
  }

  public static updatePlanIfHasAdvanced(ScheduleModelAdvanced, schedules, stopAfterString, isUnix = false): void {
    if (ScheduleModelAdvanced.blockLevel && ScheduleModelAdvanced.blockLevel.Enabled)
      ScheduleDataHelper.SetRecurringScheduleSettings(schedules.schedule, ScheduleModelAdvanced.blockLevel.FormSchedule, isUnix);
    else ScheduleDataHelper.SetRecurringEmptySchedule(schedules.schedule, isUnix);
    if (ScheduleModelAdvanced.forceFull && ScheduleModelAdvanced.forceFull.Enabled)
      ScheduleDataHelper.SetRecurringScheduleSettings(schedules.fullSchedule, ScheduleModelAdvanced.forceFull.FormSchedule, isUnix);
    else ScheduleDataHelper.SetRecurringEmptySchedule(schedules.fullSchedule, isUnix);

    if (!isUnix) {
      if (ScheduleModelAdvanced.scheduleDiff && ScheduleModelAdvanced.scheduleDiff.Enabled)
        ScheduleDataHelper.SetRecurringScheduleSettings(schedules.ScheduleDiff, ScheduleModelAdvanced.scheduleDiff.FormSchedule);
      else ScheduleDataHelper.SetRecurringEmptySchedule(schedules.ScheduleDiff);
      if (ScheduleModelAdvanced.scheduleTLog && ScheduleModelAdvanced.scheduleTLog.Enabled)
        ScheduleDataHelper.SetRecurringScheduleSettings(schedules.ScheduleTLog, ScheduleModelAdvanced.scheduleTLog.FormSchedule);
      else ScheduleDataHelper.SetRecurringEmptySchedule(schedules.ScheduleTLog);
    }
    this.setStopAfterForSchedules(schedules, stopAfterString);
  }

  public static setStopAfterForSchedules(schedules, stopAfterString): void {
    if (schedules.schedule) schedules.schedule.StopAfter = stopAfterString;
    if (schedules.fullSchedule) schedules.fullSchedule.StopAfter = stopAfterString;
    if (schedules.ScheduleDiff) schedules.ScheduleDiff.StopAfter = stopAfterString;
    if (schedules.ScheduleTLog) schedules.ScheduleTLog.StopAfter = stopAfterString;
  }

  public static SetPlanScheduleSettingsAdvanced(
    ScheduleModel: ScheduleStepValue,
    plan: any,
    step: ScheduleAdvancedStepValue,
    isUnix = false,
    isRestore = false,
    isSQL = false,
    isConsistency = false
  ): any {
    if (!plan.Schedule) plan.Schedule = { Enabled: false } as ScheduleSettings;
    if (!isRestore && !plan.ForceFullSchedule) plan.ForceFullSchedule = { Enabled: false } as ScheduleSettings;
    if (isSQL && !plan.ScheduleDiff) plan.ScheduleDiff = { Enabled: false } as ScheduleSettings;
    if (isSQL && !plan.ScheduleTLog) plan.ScheduleTLog = { Enabled: false } as ScheduleSettings;

    const schedule = isSQL || isConsistency ? plan.ForceFullSchedule : plan.Schedule;
    const fullSchedule = isSQL || isConsistency ? plan.Schedule : plan.ForceFullSchedule;
    const ScheduleDiff = plan.ScheduleDiff;
    const ScheduleTLog = plan.ScheduleTLog;
    const stopAfterDuration = moment.duration({ hours: ScheduleModel.stopAfterHours, minutes: ScheduleModel.stopAfterMinutes });
    const stopAfterString = !ScheduleModel.StopAfterEnabled ? TimeSpanMaxValue : GetTimeSpanString(stopAfterDuration);

    if (ScheduleDiff) ScheduleDiff.StopAfter = TimeSpanMaxValue;
    if (ScheduleTLog) ScheduleTLog.StopAfter = TimeSpanMaxValue;

    const scheduleType = +ScheduleType[ScheduleModel.ScheduleType];
    switch (scheduleType) {
      case ScheduleType.noschedule:
        this.updatePlanIfNoSchedule(plan, stopAfterString, isRestore);
        break;
      case ScheduleType.once:
        this.updatePlanIfOnce(
          plan,
          isRestore ? schedule : fullSchedule,
          ScheduleModel.specificDateGroup,
          stopAfterString,
          isRestore || isSQL || isConsistency
        );
        break;
      default:
        plan.IsPredefinedTemplatesSchedule = scheduleType == ScheduleType.predefined;
        if (step) {
          const schedules = { schedule, fullSchedule, ScheduleDiff, ScheduleTLog };
          if (!isRestore) this.updatePlanIfHasAdvanced(step, schedules, stopAfterString, isUnix);
          else this.updatePlanIfHasAdvancedForRestores(step, schedule, stopAfterString, isUnix);
        }
    }

    this.updatesAdvancedScheduleBeforeReturn(ScheduleModel, plan, step, isUnix);
    return plan;
  }

  public static updatesAdvancedScheduleBeforeReturn(
    ScheduleModel: ScheduleStepValue,
    plan: any,
    step: ScheduleAdvancedStepValue,
    isUnix = false
  ): void {
    plan.ForceMissedSchedule = ScheduleModel.ForceMissedSchedule;
    if (this.isBlockLvlOrForceFull(step)) {
      plan.ForceFullApplyDiffSizeCondition = false;
      plan.ForceFullDiffSizeCondition = 50;
    } else if (step && !isUnix) {
      if (!(step.ForceFullApplyDiffSizeCondition === null || step.ForceFullApplyDiffSizeCondition === undefined))
        plan.ForceFullApplyDiffSizeCondition = step.ForceFullApplyDiffSizeCondition;
      if (!(step.ForceFullDiffSizeCondition === null || step.ForceFullDiffSizeCondition === undefined))
        plan.ForceFullDiffSizeCondition = step.ForceFullDiffSizeCondition;
    }
    if (plan.Schedule) delete plan.Schedule.StopAfterTicks;
    if (plan.ForceFullSchedule) delete plan.ForceFullSchedule.StopAfterTicks;
    if (plan.ScheduleDiff) delete plan.ScheduleDiff.StopAfterTicks;
    if (plan.ScheduleTLog) delete plan.ScheduleTLog.StopAfterTicks;
    if (plan.FfiSyntheticSchedule) delete plan.FfiSyntheticSchedule.StopAfterTicks;
  }

  public static SetRecurringScheduleIfNotPeriodically(Schedule: ScheduleSettings, RecurringSchedule: RecurringSchedule): void {
    Schedule.DailyRecurrence = RecurringSchedule.DailyFrequency.PeriodOption == 'OccursEvery';
    if (RecurringSchedule.DailyFrequency.OccursAtTime) {
      const occursAtTime = GetDateFromTimeString(RecurringSchedule.DailyFrequency.OccursAtTime);
      Schedule.Hour = occursAtTime.getHours();
      Schedule.Minutes = occursAtTime.getMinutes();
    }
    let dailyRecurrencePeriod = RecurringSchedule.DailyFrequency.OccursEveryCount;
    if (RecurringSchedule.DailyFrequency.OccursEveryPeriod === 'hours') dailyRecurrencePeriod = dailyRecurrencePeriod * 60;

    Schedule.DailyRecurrencePeriod = dailyRecurrencePeriod || 0;

    if (!Schedule.DailyRecurrence) {
      Schedule.DailyFromHour = 8;
      Schedule.DailyFromMinutes = 0;
      Schedule.DailyTillHour = 6;
      Schedule.DailyTillMinutes = 0;
    } else {
      const occursEveryFrom = GetDateFromTimeString(RecurringSchedule.DailyFrequency.OccursEveryFromTime);
      Schedule.DailyFromHour = occursEveryFrom.getHours();
      Schedule.DailyFromMinutes = occursEveryFrom.getMinutes();

      const occursEveryTo = GetDateFromTimeString(RecurringSchedule.DailyFrequency.OccursEveryToTime);
      Schedule.DailyTillHour = occursEveryTo.getHours();
      Schedule.DailyTillMinutes = occursEveryTo.getMinutes();
    }
  }

  public static SetSchedulePredefinedTemplates(
    ScheduleModel: ScheduleStepValue,
    plan: any,
    model: SimpleScheduleStepValue,
    isSQL = false
  ): void {
    this.SetPlanScheduleSettingsAdvanced(ScheduleModel, plan, null);
    const schedule = isSQL ? plan.ForceFullSchedule : plan.Schedule;
    const ScheduleDiff = plan.ScheduleDiff;
    const ScheduleTLog = plan.ScheduleTLog;
    const fullSchedule = model.fullScheduleEnabled ? (isSQL ? plan.Schedule : plan.ForceFullSchedule) : schedule;
    const newRecurringSchedule = new RecurringSchedule();
    newRecurringSchedule.RecurringPeriod = RecurType.Periodically;
    newRecurringSchedule.DailyFrequency.StartFromDate = getFullDateFromDateTimeObject({
      date: model.startFromDate,
      time: model.startFromTime
    });
    if (model.fullScheduleEnabled) {
      newRecurringSchedule.DailyFrequency.OccursEveryDuration = moment.duration({
        hours: model.fullBackupEveryHours,
        minutes: model.fullBackupEveryMinutes
      });
      ScheduleDataHelper.SetRecurringScheduleSettings(fullSchedule, newRecurringSchedule);
    } else fullSchedule.Enabled = false;

    if (model.blockLevelBackup) {
      newRecurringSchedule.DailyFrequency.OccursEveryDuration = moment.duration({
        hours: model.blockLevelBackupEveryHours,
        minutes: model.blockLevelBackupEveryMinutes
      });
      ScheduleDataHelper.SetRecurringScheduleSettings(schedule, newRecurringSchedule);
    } else schedule.Enabled = false;

    if (model.scheduleDiffEnabled) {
      newRecurringSchedule.DailyFrequency.OccursEveryDuration = moment.duration({
        hours: model.differentialBackupEveryHours,
        minutes: model.differentialBackupEveryMinutes
      });
      ScheduleDataHelper.SetRecurringScheduleSettings(ScheduleDiff, newRecurringSchedule);
    } else if (ScheduleDiff) ScheduleDiff.Enabled = false;

    if (model.scheduleTLogEnabled) {
      newRecurringSchedule.DailyFrequency.OccursEveryDuration = moment.duration({
        hours: model.tLogBackupEveryHours,
        minutes: model.tLogBackupEveryMinutes
      });
      ScheduleDataHelper.SetRecurringScheduleSettings(ScheduleTLog, newRecurringSchedule);
    } else if (ScheduleTLog) ScheduleTLog.Enabled = false;

    const StopAfterDuration = moment.duration({ hours: ScheduleModel.stopAfterHours, minutes: ScheduleModel.stopAfterMinutes });
    const stopAfterString = !ScheduleModel.StopAfterEnabled ? TimeSpanMaxValue : GetTimeSpanString(StopAfterDuration);
    schedule.StopAfter = stopAfterString;
    fullSchedule.StopAfter = stopAfterString;
    if (ScheduleDiff) ScheduleDiff.StopAfter = stopAfterString;
    if (ScheduleTLog) ScheduleTLog.StopAfter = stopAfterString;
  }

  public static encodeNumberToGuid(value: number, mode: EncodeMode = 'hex'): string {
    if (!Number.isInteger(value) || value < 0) {
      throw new Error('Value must be a non-negative integer');
    }

    if (mode === 'int64' && value > Number.MAX_SAFE_INTEGER) {
      throw new Error('Value exceeds MAX_SAFE_INTEGER');
    }

    const bytes = new Uint8Array(16); // всё заполнено нулями

    switch (mode) {
      case 'hex': {
        const hex = value.toString(16).padStart(8, '0');
        return `${hex}-0000-0000-0000-000000000000`;
      }

      case 'int32': {
        bytes[0] = value & 0xff;
        bytes[1] = (value >> 8) & 0xff;
        bytes[2] = (value >> 16) & 0xff;
        bytes[3] = (value >> 24) & 0xff;
        break;
      }

      case 'int64': {
        let v = BigInt(value);
        for (let i = 0; i < 8; i++) {
          bytes[i] = Number(v & 0xffn);
          v >>= 8n;
        }
        break;
      }
    }

    return this.bytesToGuid(bytes);
  }

  private static bytesToGuid(bytes: Uint8Array): string {
    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  private static isBlockLvlOrForceFull(step: ScheduleAdvancedStepValue): boolean {
    return step && (!step.blockLevel || !step.blockLevel.Enabled) && (!step.forceFull || !step.forceFull.Enabled);
  }
}
